/**
 * Kleda Client App - Ondertapijt Upsell View
 * Standalone page focused entirely on the underlay upsell
 *
 * Stripe-level UX: Clean, focused, conversion-optimized
 */

import { useState, useEffect, useRef } from 'react'
import type { OrderData, VoorinspectieItem } from '../types'
import { confirmOndertapijt, nabestelOndertapijt, refreshPaymentLink } from '../api'
import { trackOndertapijtViewed, trackOndertapijtConfirmed } from '../analytics'
import { PageLayout } from './Layout'
import { Card, Icon, Modal, NummerBadge, TrustLine } from './UI'
import { fetchVoorinspectieThumbnails } from '../api'

// =============================================================================
// UTILITIES
// =============================================================================

function formatPrice(amount: number | undefined): string {
  if (amount === undefined || amount === null) return '€ 0,00'
  return `€ ${amount.toFixed(2).replace('.', ',')}`
}

// After-flow (order al afgeleverd): het ondertapijt wordt apart opgestuurd en
// verzendkosten worden altijd gerekend (besluit 06-08 — de aanbieding gaat
// normaal gratis met het kleed mee, apart versturen kost wél verzendkosten).
// Moet gelijk blijven aan SHIPPING_COST_CENTS in office-app
// api/_lib/nabestel-ondertapijt.ts; de server is leidend, dit is weergave.
const VERZENDKOSTEN = 6.5

function formatM2(m2: number | undefined): string {
  if (m2 === undefined || m2 === null) return '0 m²'
  // Round to 2 decimals to avoid floating point precision issues
  const rounded = Math.round(m2 * 100) / 100
  return `${rounded.toFixed(2).replace('.', ',')} m²`
}

/**
 * Calculate hours remaining until a deadline date (end of day).
 * Returns null if no deadline or deadline has passed.
 */
function getHoursUntilDeadline(deadlineStr: string | undefined): number | null {
  if (!deadlineStr) return null
  const deadline = new Date(deadlineStr + 'T23:59:59')
  const now = new Date()
  const diffMs = deadline.getTime() - now.getTime()
  if (diffMs <= 0) return null
  return Math.floor(diffMs / (1000 * 60 * 60))
}

/**
 * Format deadline as human-readable countdown text.
 */
function formatCountdown(hours: number): string {
  if (hours >= 48) {
    const days = Math.floor(hours / 24)
    return `nog ${days} dagen`
  }
  if (hours >= 24) {
    return 'nog 1 dag'
  }
  if (hours >= 1) {
    return `nog ${hours} uur`
  }
  return 'bijna verlopen'
}

// =============================================================================
// ITEM SELECTION CARD
// =============================================================================

interface ItemSelectCardProps {
  item: VoorinspectieItem
  selected: boolean
  onToggle: () => void
  volgnummer?: number
  thumbnailUrl?: string
}

function ItemSelectCard({ item, selected, onToggle, volgnummer, thumbnailUrl }: ItemSelectCardProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`
        w-full text-left p-lg rounded-kleda-lg transition-all duration-200 shadow-card
        ${selected
          ? 'bg-surface-soft border-2 border-primary'
          : 'bg-white border-2 border-track hover:border-primary/40'
        }
      `}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-md min-w-0">
          {/* Custom checkbox */}
          <div className={`
            w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0
            ${selected
              ? 'bg-primary border-primary'
              : 'bg-white border-border-subtle'
            }
          `}>
            {selected && (
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>

          {/* Thumbnail van het kleed zelf — zodat duidelijk is welk kleed het is */}
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt={`Foto van vloerkleed ${volgnummer ?? ''}`}
              loading="lazy"
              className="w-12 h-12 object-cover rounded-kleda-sm flex-shrink-0"
            />
          )}

          <div className="min-w-0">
            <p className="flex items-center gap-xs text-body font-semibold text-ink">
              {volgnummer != null && <NummerBadge nummer={volgnummer} />}
              <span className="truncate">{item.name}</span>
            </p>
            <p className="text-body-sm text-ink-muted">{formatM2(item.m2)}</p>
          </div>
        </div>

        <div className="text-right flex-shrink-0 ml-sm">
          <p className="text-body-sm text-ink-muted line-through">
            {formatPrice(item.ondertapijtPrijs)}
          </p>
          <p className="text-body font-bold text-primary">
            {formatPrice(item.ondertapijtPrijsMetKorting)}
          </p>
        </div>
      </div>
      {item.tweedeligOndertapijt && (
        <div className="mt-xs">
          <span className="inline-flex items-center gap-xs px-sm py-xs bg-blue-50 border border-blue-200 rounded text-body-sm text-blue-700">
            Dit ondertapijt bestaat uit 2 delen
          </span>
        </div>
      )}
    </button>
  )
}

// =============================================================================
// ONDERTAPIJT VIEW
// =============================================================================

interface OndertapijtViewProps {
  order: OrderData
  token: string
  onRefresh: () => void
  onBackToReport: () => void
}

export function OndertapijtView({ order, token, onRefresh, onBackToReport }: OndertapijtViewProps) {
  const data = order.voorinspectie?.ondertapijt

  // After-flow: order is al afgeleverd. Zelfde selectie-UI, maar de bevestiging
  // maakt een nieuwe webshop-order (nabestellen + PostNL) in plaats van het
  // ondertapijt aan de lopende order toe te voegen. Gelijk aan
  // TERMINALE_STATUSSEN in office-app nabestel-ondertapijt.ts — Afgerond
  // is dezelfde eindstand als Teruggebracht, alleen later.
  const isAfter = order.status === 'Teruggebracht' || order.status === 'Afgerond'

  // Bewust GEEN vooraf-selectie: de klant moet zelf aanvinken welk kleed een
  // ondertapijt op maat krijgt. Voorkomt per ongeluk toevoegen van een
  // niet-annuleerbaar, op-maat-gemaakt product.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showConfirm, setShowConfirm] = useState(false)
  const [understood, setUnderstood] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const hasTrackedView = useRef(false)

  // Kleed-thumbnails (CompanyCam) async bijladen — zelfde bron als het rapport
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({})
  useEffect(() => {
    let active = true
    fetchVoorinspectieThumbnails(token).then(result => {
      if (active) setThumbnails(result.thumbnails)
    })
    return () => { active = false }
  }, [token])

  // itemId → volgnummer, consistent met de nummer-badges in het rapport
  const volgnummers: Record<string, number> = {}
  ;(order.voorinspectie?.items ?? []).forEach((item, i) => { volgnummers[item.id] = i + 1 })

  // Track view once
  useEffect(() => {
    if (!hasTrackedView.current && data?.eligible && !data?.alreadyOrdered) {
      trackOndertapijtViewed(
        order.werkOrderId,
        data.totalPriceWithDiscountInclVat,
        data.savingsInclVat,
        data.items.length
      )
      hasTrackedView.current = true
    }
  }, [data, order.werkOrderId])

  if (!data) {
    return (
      <PageLayout orderNummer={order.werkOrderNummer}>
        <div className="text-center py-xl">
          <p className="text-body text-ink-muted">Geen ondertapijt aanbieding beschikbaar.</p>
          <button
            onClick={onBackToReport}
            className="mt-md text-primary hover:text-primary-dark font-medium"
          >
            ← Terug naar je inspectierapport
          </button>
        </div>
      </PageLayout>
    )
  }

  // All items unavailable (round rugs or out-of-stock rolls)
  if (!data.alreadyOrdered && !data.deadlinePassed && data.items.length === 0 && (data.unavailableItemCount ?? 0) > 0) {
    return (
      <PageLayout orderNummer={order.werkOrderNummer}>
        <div className="max-w-md mx-auto text-center py-xl">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-50 text-amber-500 mb-lg">
            <Icon name="clock" className="w-10 h-10" />
          </div>
          <h1 className="font-heading text-heading-main text-ink mb-sm">
            Tijdelijk niet beschikbaar
          </h1>
          <p className="text-body-intro text-ink-muted mb-lg">
            Ondertapijt op maat is voor jouw vloerkleed{(data.unavailableItemCount ?? 0) > 1 ? 'en' : ''} momenteel niet leverbaar. We verwachten binnenkort weer voorraad — bij een volgende reiniging kun je het alsnog toevoegen!
          </p>
          <button onClick={onBackToReport} className="text-primary hover:text-primary-dark font-medium transition-colors">
            ← Bekijk je bestelling
          </button>
        </div>
      </PageLayout>
    )
  }

  // Calculate dynamic totals
  const selectedItems = data.items.filter(item => selectedIds.has(item.id))
  const selectedTotal = selectedItems.reduce((sum, item) => sum + (item.ondertapijtPrijs || 0), 0)
  const selectedTotalWithDiscount = selectedItems.reduce((sum, item) => sum + (item.ondertapijtPrijsMetKorting || 0), 0)
  const selectedSavings = selectedTotal - selectedTotalWithDiscount

  // After-flow: altijd verzendkosten (server is leidend)
  const verzendkosten = isAfter && selectedIds.size > 0 ? VERZENDKOSTEN : 0
  const teBetalen = selectedTotalWithDiscount + verzendkosten

  const toggleItem = (id: string) => {
    const newSet = new Set(selectedIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedIds(newSet)
  }

  const handleConfirm = async () => {
    if (selectedIds.size === 0) {
      setError('Selecteer minimaal één vloerkleed')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      if (isAfter) {
        // After-flow: nieuwe webshop-order + Mollie-betaallink; door naar betalen.
        const response = await nabestelOndertapijt(token, Array.from(selectedIds))
        if (response.success) {
          trackOndertapijtConfirmed(
            order.werkOrderId,
            teBetalen,
            selectedSavings,
            Array.from(selectedIds)
          )
          if (response.paymentUrl) {
            window.location.assign(response.paymentUrl)
            return // redirect onderweg
          }
          // Geen link teruggekregen: bestelling staat wél, link volgt per mail.
          setSuccess(true)
        } else {
          setError(response.message || 'Er ging iets mis')
        }
        return
      }

      const response = await confirmOndertapijt({
        token,
        confirm: true,
        itemIds: Array.from(selectedIds)
      })

      if (response.success) {
        setSuccess(true)
        trackOndertapijtConfirmed(
          order.werkOrderId,
          selectedTotalWithDiscount,
          selectedSavings,
          Array.from(selectedIds)
        )
        // Betaallink synchroon bijwerken vóór de refetch (zie refreshPaymentLink).
        await refreshPaymentLink(token)
        onRefresh()
      } else {
        setError(response.message || 'Er ging iets mis')
      }
    } catch {
      setError('Er ging iets mis. Probeer het later opnieuw.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Deadline & countdown
  const hoursLeft = getHoursUntilDeadline(data.orderDeadline)
  const hasDeadline = data.orderDeadline != null

  // Already ordered state
  if (data.alreadyOrdered) {
    return (
      <PageLayout orderNummer={order.werkOrderNummer}>
        <div className="max-w-md mx-auto text-center py-xl">
          {/* Success icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary/10 text-secondary mb-lg">
            <Icon name="check" className="w-10 h-10" />
          </div>

          <h1 className="font-heading text-heading-main text-ink mb-sm">
            Ondertapijt toegevoegd!
          </h1>
          <p className="text-body-intro text-ink-muted mb-lg">
            Je bestelling bevat al ondertapijt op maat. We snijden het iets kleiner dan je vloerkleed (tot 5 cm per kant) zodat het onzichtbaar eronder past, en leveren het samen af.
          </p>

          <button
            onClick={onBackToReport}
            className="text-primary hover:text-primary-dark font-medium transition-colors"
          >
            ← Bekijk je inspectierapport
          </button>
        </div>
      </PageLayout>
    )
  }

  // Success state after ordering
  if (success) {
    return (
      <PageLayout orderNummer={order.werkOrderNummer}>
        <div className="max-w-md mx-auto text-center py-xl">
          {/* Animated success */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary/10 text-secondary mb-lg animate-bounce-once">
            <Icon name="check" className="w-10 h-10" />
          </div>

          <h1 className="font-heading text-heading-main text-ink mb-sm">
            Gelukt! 🎉
          </h1>
          <p className="text-body-intro text-ink-muted mb-md">
            {isAfter
              ? 'Je bestelling staat klaar. Je ontvangt een e-mail met de betaallink; na betaling snijden we het ondertapijt op maat en sturen we het op.'
              : 'Je ondertapijt op maat is toegevoegd aan je bestelling.'}
          </p>

          <Card className="text-left mb-lg">
            <div className="space-y-sm text-body-sm">
              <div className="flex items-center gap-sm text-ink-soft">
                <span className="text-primary font-semibold">✓</span>
                We snijden het op maat — iets kleiner dan je kleed, zodat het onzichtbaar eronder past
              </div>
              <div className="flex items-center gap-sm text-ink-soft">
                <span className="text-primary font-semibold">✓</span>
                {isAfter
                  ? 'We sturen het per PostNL naar je op'
                  : 'Je ontvangt alles samen bij aflevering'}
              </div>
            </div>
          </Card>

          <button
            onClick={onBackToReport}
            className="text-primary hover:text-primary-dark font-medium transition-colors"
          >
            ← Bekijk je inspectierapport
          </button>
        </div>
      </PageLayout>
    )
  }

  // Main upsell view
  return (
    <PageLayout orderNummer={order.werkOrderNummer}>
      {/* Discount badge - floating with urgency */}
      <div className="flex justify-center mb-md">
        <span className="inline-flex items-center gap-xs px-md py-sm bg-gradient-to-r from-primary/10 to-secondary/10 text-primary text-body font-semibold rounded-full border border-primary/20">
          <Icon name="gift" className="text-primary" />
          {isAfter
            ? `${data.discountPercentage}% korting · speciaal voor jouw net gereinigde kleed`
            : `${data.discountPercentage}% korting · alleen zolang je kleed in behandeling is`}
        </span>
      </div>

      {/* Countdown banner when delivery is scheduled */}
      {!isAfter && hasDeadline && hoursLeft != null && (
        <div className="flex justify-center mb-md">
          <div className={`inline-flex items-center gap-sm px-md py-sm rounded-kleda-md border ${
            hoursLeft <= 24
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            <Icon name="clock" className={hoursLeft <= 24 ? 'text-red-500' : 'text-amber-600'} />
            <span className="text-body-sm font-medium">
              Je aflevering is ingepland — bestel binnen <strong>{formatCountdown(hoursLeft)}</strong> om het mee te krijgen
            </span>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="text-center mb-lg">
        <h1 className="font-heading text-heading-main text-ink mb-sm leading-tight">
          Extra lang genieten van je schone vloerkleed
        </h1>
        <p className="text-body-intro text-ink-muted max-w-md mx-auto">
          Bescherm je vers gereinigde kleed met een ondertapijt op maat — speciaal gesneden voor jouw vloerkleed.
          {isAfter && ' We sturen het per PostNL naar je op (€6,50 verzendkosten).'}
        </p>
      </div>

      {/* Product image - smaller on desktop, full width on mobile */}
      <div className="mb-lg flex justify-center">
        <div className="w-full md:w-2/3 lg:w-1/2 rounded-kleda-xl overflow-hidden shadow-lg">
          <img
            src="https://kleda.nl/images/ondertapijt.jpg"
            alt="Antislip ondertapijt - close-up van de structuur"
            className="w-full h-40 md:h-56 object-cover"
          />
        </div>
      </div>

      {/* Product story */}
      <Card className="mb-lg">
        <div className="text-center mb-md">
          <p className="text-body-intro font-medium text-ink">
            Het ondertapijt dat onze specialisten zelf gebruiken
          </p>
          <p className="text-body text-ink-muted mt-sm">
            De Kleda Base is een professioneel ondertapijt met tweezijdige grip — de bovenkant houdt je kleed op z'n plek, de onderkant beschermt je vloer. Geen tape, geen clips, geen gedoe.
          </p>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-sm">
          <span className="inline-flex items-center gap-xs px-sm py-xs bg-surface-soft rounded-full text-body-sm text-ink-muted">
            Duits fabricaat
          </span>
          <span className="inline-flex items-center gap-xs px-sm py-xs bg-surface-soft rounded-full text-body-sm text-ink-muted">
            <svg className="w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
            OEKO-TEX-gecertificeerd
          </span>
          <span className="inline-flex items-center gap-xs px-sm py-xs bg-surface-soft rounded-full text-body-sm text-ink-muted">
            PVC-vrij
          </span>
        </div>
      </Card>

      {/* Partial unavailability banner */}
      {(data.unavailableItemCount ?? 0) > 0 && data.items.length > 0 && (
        <div className="flex items-start gap-sm px-md py-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-kleda-md mb-lg">
          <Icon name="alert" className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
          <p className="text-body-sm">
            Voor {data.unavailableItemCount} van je vloerkleed{(data.unavailableItemCount ?? 0) > 1 ? 'en' : ''} is ondertapijt momenteel niet leverbaar vanwege de afmetingen. Hieronder zie je de kleden waarvoor het wél beschikbaar is.
          </p>
        </div>
      )}

      {/* Item selection */}
      <div className="mb-lg">
        <p className="section-label mb-xs">
          Selecteer je vloerkleden
        </p>
        <p className="text-body-sm text-ink-muted mb-sm">
          Vink de vloerkleden aan waarvoor je een ondertapijt op maat wilt.
        </p>
        <div className="space-y-sm">
          {data.items.map(item => (
            <ItemSelectCard
              key={item.id}
              item={item}
              selected={selectedIds.has(item.id)}
              onToggle={() => toggleItem(item.id)}
              volgnummer={volgnummers[item.id]}
              thumbnailUrl={thumbnails[item.id]}
            />
          ))}
        </div>
      </div>

      {/* Prijs-breakdown — alleen zodra er iets gekozen is */}
      {selectedIds.size > 0 && (
        <Card className="mb-md">
          <div className="space-y-sm">
            <div className="flex items-center justify-between text-body">
              <span className="text-ink-muted">Normale prijs</span>
              <span className="text-ink-muted line-through">{formatPrice(selectedTotal)}</span>
            </div>
            <div className="flex items-center justify-between text-body">
              <span className="text-secondary font-medium">Jouw korting ({data.discountPercentage}%)</span>
              <span className="text-secondary font-medium">- {formatPrice(selectedSavings)}</span>
            </div>
            {isAfter && (
              <div className="flex items-center justify-between text-body">
                <span className="text-ink-muted">Verzendkosten (PostNL)</span>
                <span className="text-ink">{formatPrice(verzendkosten)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-sm border-t border-hairline">
              <span className="text-body font-semibold text-ink">Totaal</span>
              <span className="text-heading-sm font-bold text-primary">{formatPrice(isAfter ? teBetalen : selectedTotalWithDiscount)}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Op maat / definitief — stille regel; de expliciete bevestiging zit in de modal */}
      <p className="text-[13px] text-ink-faint mb-md">
        Op maat gemaakt — kan na bestelling niet geannuleerd of geruild worden.
      </p>

      {/* CTA → opent bevestiging (echte bestelling gebeurt in de modal) */}
      <button
        onClick={() => setShowConfirm(true)}
        disabled={selectedIds.size === 0}
        className="btn-primary w-full text-lg py-lg mb-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {selectedIds.size === 0 ? 'Selecteer een vloerkleed' : 'Ondertapijt toevoegen →'}
      </button>

      <div className="flex justify-center mb-lg">
        <TrustLine />
      </div>

      {/* Waarom Kleda Base — onderbouwing voor wie nog twijfelt */}
      <Card className="mb-lg">
        <p className="section-label mb-md">
          Waarom Kleda Base?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
          <div className="flex items-start gap-sm">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Icon name="shield" />
            </div>
            <div>
              <p className="text-body font-medium text-ink">Tweezijdige grip</p>
              <p className="text-body-sm text-ink-muted">Houdt je kleed op z'n plek en beschermt je vloer</p>
            </div>
          </div>
          <div className="flex items-start gap-sm">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Icon name="flame" />
            </div>
            <div>
              <p className="text-body font-medium text-ink">Vloerverwarming? Geen probleem</p>
              <p className="text-body-sm text-ink-muted">Warmtegeleidend — geschikt voor alle harde vloeren</p>
            </div>
          </div>
          <div className="flex items-start gap-sm">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Icon name="cloud" />
            </div>
            <div>
              <p className="text-body font-medium text-ink">Extra comfort</p>
              <p className="text-body-sm text-ink-muted">3 mm dik polyestervlies — zachter loopgevoel</p>
            </div>
          </div>
          <div className="flex items-start gap-sm">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Icon name="truck" />
            </div>
            <div>
              <p className="text-body font-medium text-ink">{isAfter ? 'Op maat & thuisbezorgd' : 'Op maat & samen afgeleverd'}</p>
              <p className="text-body-sm text-ink-muted">
                {isAfter
                  ? 'Op maat van jouw kleed gesneden — tot 5 cm per kant kleiner, zodat het onzichtbaar eronder past. Per PostNL bij je thuisbezorgd'
                  : 'Op maat van jouw kleed gesneden — tot 5 cm per kant kleiner, zodat het onzichtbaar eronder past. Samen met je schone kleed afgeleverd'}
              </p>
            </div>
          </div>
        </div>
        <p className="text-body-sm text-ink-muted mt-md">
          Wasbaar op 30°C. Gaat langer mee dan je vloerkleed.
        </p>
      </Card>

      {/* Link back to report */}
      <div className="text-center border-t border-hairline pt-lg">
        <p className="text-body-sm text-ink-muted mb-xs">Of bekijk eerst je volledige rapport</p>
        <button
          onClick={onBackToReport}
          className="text-primary hover:text-primary-dark font-medium transition-colors"
        >
          ← Naar mijn inspectierapport
        </button>
      </div>

      {/* Bevestiging — expliciet, geïnformeerd, op maat & definitief */}
      <Modal
        isOpen={showConfirm}
        onClose={() => { setShowConfirm(false); setUnderstood(false); setError(null) }}
        title="Ondertapijt op maat toevoegen?"
      >
        {/* Wat je precies bestelt */}
        <div className="space-y-xs mb-md">
          {selectedItems.map(item => (
            <div key={item.id} className="flex items-center justify-between text-body">
              <span className="text-ink">{item.name}</span>
              <span className="text-ink font-medium">{formatPrice(item.ondertapijtPrijsMetKorting)}</span>
            </div>
          ))}
          {isAfter && (
            <div className="flex items-center justify-between text-body">
              <span className="text-ink">Verzendkosten (PostNL)</span>
              <span className="text-ink font-medium">{formatPrice(verzendkosten)}</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-sm border-t border-hairline">
            <span className="text-body font-semibold text-ink">Totaal (incl. btw)</span>
            <span className="text-heading-sm font-bold text-primary">{formatPrice(isAfter ? teBetalen : selectedTotalWithDiscount)}</span>
          </div>
        </div>

        {/* Waarschuwing: op maat = definitief */}
        <div className="bg-amber-50 border border-amber-200 rounded-kleda-md p-md mb-md">
          <div className="flex items-start gap-sm">
            <div className="text-amber-600 mt-0.5">
              <Icon name="alert" />
            </div>
            <div>
              <p className="text-body font-semibold text-amber-900">Op maat gemaakt — definitief</p>
              <p className="text-body-sm text-amber-800 mt-xs">
                We snijden het ondertapijt op maat van jouw vloerkleed — tot 5 cm per kant kleiner, zodat het goed onder het kleed past. Daarom kun je het na bestelling niet meer annuleren, ruilen of terugnemen.
              </p>
            </div>
          </div>
        </div>

        {/* Verplicht vinkje — bewuste, geïnformeerde bevestiging */}
        <label className="flex items-start gap-sm mb-md cursor-pointer">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => { setUnderstood(e.target.checked); setError(null) }}
            disabled={isSubmitting}
            className="mt-1 w-4 h-4 rounded border-border-subtle text-primary focus:ring-primary"
          />
          <span className="text-body text-ink">
            Ik begrijp dat het ondertapijt op maat gemaakt wordt en niet geannuleerd of geruild kan worden.
          </span>
        </label>

        {isAfter && (
          <p className="text-body-sm text-ink-muted mb-md">
            Na bevestigen ga je direct naar de betaalpagina. Zodra je betaald hebt, snijden we het op maat en sturen we het op.
          </p>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-md py-sm rounded-kleda-md mb-md text-body">
            {error}
          </div>
        )}

        {/* Acties */}
        <div className="flex gap-sm">
          <button
            type="button"
            onClick={() => { setShowConfirm(false); setUnderstood(false); setError(null) }}
            disabled={isSubmitting}
            className="btn-outline flex-1"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!understood || isSubmitting}
            className="btn-primary flex-1"
          >
            {isSubmitting ? 'Bezig...' : isAfter ? 'Bestel en betaal' : 'Ja, bestel definitief'}
          </button>
        </div>
      </Modal>
    </PageLayout>
  )
}
